import { get } from "@ember/object"
import { inject as service } from "@ember/service"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Deal, FiscalGroup, FiscalGroupInvoice } from "@shared/models/fiscal-group"
import type { Group } from "@shared/models/group"
import type { KbEntry } from "@shared/models/kb-entry"
import type { Planbook, PlanbookSummary } from "@shared/models/planbook"
import type { UserSummary } from "@shared/models/user"
import type EmberStore from "cc-frontend/services/ember-store"
import { Resource } from "ember-could-get-used-to-this"
import { isNil, map, reject } from "lodash-es"
import { all } from "rsvp"
import { tracked } from "tracked-built-ins"

type AllModels =
  | CardStack
  | Group
  | Course
  | PlanbookSummary
  | UserSummary
  | FiscalGroupInvoice
  | FiscalGroup
  | KbEntry
  | Deal

type positional =
  | [model: CardStack, relationName: keyof CardStack["relationships"], type?: string]
  | [model: UserSummary, relationName: keyof UserSummary["relationships"], type?: string]
  | [model: PlanbookSummary, relationName: keyof PlanbookSummary["relationships"], type?: string]
  | [model: Planbook, relationName: keyof Planbook["relationships"], type?: string]
  | [model: Group, relationName: keyof Group["relationships"], type?: string]
  | [model: Course, relationName: keyof Course["relationships"], type?: string]
  | [model: FiscalGroup, relationName: keyof FiscalGroup["relationships"], type?: string]
  | [model: KbEntry, relationName: keyof KbEntry["relationships"], type?: string]
  | [model: null, relationName: any, type?: string]

interface Args {
  positional: positional
}

export default class HasManyAltogether<T extends AllModels> extends Resource<Args["positional"]> {
  @service("ember-store") declare store: EmberStore

  declare model: Args["positional"][0] | null
  declare relationName: Args["positional"][1] | null
  declare modelType: string | null | undefined

  @tracked isPending = false
  @tracked isSettled = false
  @tracked isRejected = false
  @tracked isFulfilled = false
  @tracked content: Array<T> | null = null

  setup() {
    if (this.args.positional) {
      this.model = this.args.positional[0]
      this.relationName = this.args.positional[1]
      this.modelType = this.args.positional[2]
    }
    this._find()
  }

  _find() {
    if (isNil(this.relationName) || isNil(this.model)) return
    this.isPending = true
    this.isFulfilled = false
    this.isSettled = false
    let relationships = get(this.model.relationships[this.relationName], "data")
    all<T>(
      map(
        reject(relationships, (relation) => relation.id == null || relation.id === ""),
        (relation) => this.store.find(this.modelType || relation.type, relation.id)
      )
    )
      .then((docs: Array<T>) => {
        this.isPending = false
        this.isFulfilled = true
        this.content = docs
      })
      .catch(() => {
        this.isRejected = true
      })
      .finally(() => {
        this.isSettled = true
      })
  }

  update() {
    if (this.args.positional) {
      this.model = this.args.positional[0]
      this.relationName = this.args.positional[1]
      this.modelType = this.args.positional[2]
    }
    // Ideally, we'd read the new relations to see if they changed, but due to the read before write problem
    // we can't do that.
    this._find()
  }

  teardown() {
    // we'll unsubscribe from it here.
  }
}
